/**
 * Mentions Composable
 * Integrates Tribute.js with Trix editor for @mentions functionality
 */

import Tribute from 'tributejs';

/**
 * Default mention configuration
 */
const DEFAULT_CONFIG = {
  trigger: '@',
  menuItemLimit: 5,
  noMatchTemplate: () => '<li class="tribute-no-match">No matches found</li>',
  containerClass: 'tribute-container',
  itemClass: 'tribute-item',
  selectClass: 'tribute-highlight',
  menuShowMinLength: 1
};

/**
 * Initialize mentions for a Trix editor
 * @param {HTMLElement} editorElement - The trix-editor element
 * @param {Array} users - Array of user objects with name, handle, email properties
 * @param {Object} options - Additional Tribute options
 * @returns {Object} Object with tribute instance and cleanup function
 */
export function useMentions(editorElement, users = [], options = {}) {
  if (!editorElement) {
    console.warn('useMentions: Invalid editor element');
    return { tribute: null, cleanup: () => {} };
  }

  // Detect duplicate names for handle display
  const nameCount = {};
  users.forEach(user => {
    nameCount[user.name] = (nameCount[user.name] || 0) + 1;
  });

  const tributeConfig = {
    ...DEFAULT_CONFIG,
    values: users,

    // Template for the selected mention
    selectTemplate: (item) => {
      if (!item || !item.original) return '';

      const user = item.original;
      // Use handle if there are duplicate names
      const displayName = nameCount[user.name] > 1 ? user.handle : user.name;

      return `<span contenteditable="false" class="mention" data-user-id="${user.id || ''}" data-user-handle="${user.handle || ''}">@${displayName}</span>`;
    },

    // Template for menu items
    menuItemTemplate: (item) => {
      if (!item || !item.original) return '';

      const user = item.original;
      // Show handle if there are duplicate names
      const displayText = nameCount[user.name] > 1
        ? `@${user.handle}`
        : `@${user.name}`;

      return `<span class="mention-item">${displayText}</span>`;
    },

    // Search against both name and handle
    lookup: (item) => {
      return `${item.name} ${item.handle}`;
    },

    // Fill on select
    fillAttr: 'name',

    ...options
  };

  // Create Tribute instance
  const tribute = new Tribute(tributeConfig);

  // Attach to editor
  tribute.attach(editorElement);

  // Handle Trix-specific adjustments for mentions
  function handleTrixChange(event) {
    // Ensure mention spans stay non-editable
    const mentions = editorElement.querySelectorAll('.mention');
    mentions.forEach(mention => {
      mention.setAttribute('contenteditable', 'false');
    });
  }

  editorElement.addEventListener('trix-change', handleTrixChange);

  // Cleanup function
  function cleanup() {
    tribute.detach(editorElement);
    editorElement.removeEventListener('trix-change', handleTrixChange);
  }

  return {
    tribute,
    cleanup,
    // Utility to extract mentions from content
    extractMentions: () => extractMentionsFromEditor(editorElement)
  };
}

/**
 * Extract all mentions from an editor element
 * @param {HTMLElement} editorElement - The editor element
 * @returns {Array} Array of mention data objects
 */
export function extractMentionsFromEditor(editorElement) {
  const mentions = [];
  const spans = editorElement.querySelectorAll('.mention');

  spans.forEach(span => {
    const text = span.textContent || span.innerText;
    const userId = span.dataset.userId;
    const userHandle = span.dataset.userHandle;

    mentions.push({
      text: text.replace(/^@/, ''),
      userId,
      userHandle
    });
  });

  return mentions;
}

/**
 * Extract mention text from editor for form submission
 * @param {HTMLElement} editorElement - The editor element
 * @returns {Array} Array of mention strings (e.g., ['@john', '@jane'])
 */
export function extractMentionStrings(editorElement) {
  const spans = editorElement.querySelectorAll('.mention');
  return Array.from(spans).map(span => span.textContent || span.innerText);
}

/**
 * Update mention users list
 * @param {Object} tribute - Tribute instance
 * @param {Array} users - New users array
 */
export function updateMentionUsers(tribute, users) {
  if (tribute && tribute.collection && tribute.collection[0]) {
    tribute.collection[0].values = users;
  }
}

export default useMentions;
